import JSPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { SupportedFilesType } from 'store/ducks/file-generation/types';
import Logo from 'static/images/logo-ebury.png';
import { json2csv } from './array';
import { StatementsPreparedToExport } from 'store/ducks/exports/types';

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

interface ExportAsPDF {
  (
    transactions: StatementsPreparedToExport,
    previousDay: string,
    previousDayValue: string,
    total?: string
  ): Promise<Blob>;
}

interface ExportAsCSV {
  (
    transactions: StatementsPreparedToExport,
    previousDay: string,
    previousDayValue: string,
    total?: string
  ): Promise<Blob>;
}

const exportCSVColumns = [
  { header: 'ID', dataKey: 'id' },
  { header: 'Date', dataKey: 'date' },
  { header: 'Hour', dataKey: 'hour' },
  { header: 'Description', dataKey: 'description' },
  { header: 'Status', dataKey: 'status' },
  { header: 'Transfer Type', dataKey: 'transferType' },
  { header: 'Transfer ID', dataKey: 'transferId' },
  { header: 'Origin_Account ID', dataKey: 'originAccountId' },
  { header: 'Origin_Client ID', dataKey: 'originClientId' },
  { header: 'Origin_Client name', dataKey: 'originClientName' },
  { header: 'Origin_Document', dataKey: 'originDocument' },
  { header: 'Origin_Bank', dataKey: 'originBank' },
  { header: 'Origin_Agency', dataKey: 'originAgency' },
  { header: 'Origin_Account', dataKey: 'originAccount' },
  { header: 'Destination_Account ID', dataKey: 'destinationAccountId' },
  { header: 'Destination_Client ID', dataKey: 'destinationClientId' },
  { header: 'Destination_ Client name', dataKey: 'destinationClientName' },
  { header: 'Destination_Document', dataKey: 'destinationDocument' },
  { header: 'Destination_Bank', dataKey: 'destinationBank' },
  { header: 'Destination_Agency', dataKey: 'destinationAgency' },
  { header: 'Destination_Account', dataKey: 'destinationAccount' },
  { header: 'Amount', dataKey: 'amountFormatted' },
  { header: 'Balance', dataKey: 'balanceFormatted' },
];

const exportPDFColumns = [
  { header: 'ID', dataKey: 'id' },
  { header: 'Date', dataKey: 'date' },
  { header: 'Hour', dataKey: 'hour' },
  { header: 'Description', dataKey: 'description' },
  { header: 'Transfer Type', dataKey: 'transferType' },
  { header: 'Amount', dataKey: 'amountFormatted' },
  { header: 'Balance', dataKey: 'balanceFormatted' },
];

export const saveFile = (file: Blob, filename: string, autoBom = false) => {
  saveAs(file, filename, { autoBom });
};

const imageToBase64 = async (urlOrImage: string) => {
  const response = await fetch(urlOrImage);
  const arrayBuffer = await response.arrayBuffer();
  return Buffer.from(arrayBuffer).toString('base64');
};

export const generateAsyncPDF: ExportAsPDF = async (
  transactions,
  previousDay,
  previousDayValue
): Promise<Blob> => {
  const PDFDoc = new JSPDF();

  const base64Logo = await imageToBase64(Logo);

  const transactionsRows = previousDayValue
    ? [
        ...transactions,
        {
          description: 'Previous Day',
          date: previousDay,
          balanceFormatted: previousDayValue,
        },
      ]
    : transactions;

  PDFDoc.addImage(base64Logo, 'PNG', 12, 10, 30, 13);

  PDFDoc.setFontSize(10);
  PDFDoc.setFont(undefined, 'bold');
  PDFDoc.setTextColor('#3c3c3c');

  PDFDoc.text('Report processed in', 163, 16, '', '', 'right');

  PDFDoc.setFont(undefined, 'normal');
  PDFDoc.setTextColor('#989898');

  PDFDoc.text(
    dayjs().format('DD/MM/YYYY - hh:mm:ss'),
    163,
    21,
    '',
    '',
    'right'
  );

  PDFDoc.setFontSize(10);
  autoTable(PDFDoc, {
    startY: 32,
    headStyles: {
      fillColor: '#094358',
      textColor: '#FFFFFF',
    },
    body: transactionsRows as unknown as RowInput[],
    columns: exportPDFColumns,
    styles: { fontSize: 9 },
  });

  const pdfOutput = PDFDoc.output('blob');

  return pdfOutput;
};

export const generateAsyncCSV: ExportAsCSV = async (
  transactions,
  previousDay,
  previousDayValue
  // totalValue
) => {
  const transactionsRows = previousDayValue
    ? [
        ...transactions,
        {
          Description: 'Previous Day',
          Date: previousDay,
          BalanceFormatted: previousDayValue,
        },
      ]
    : transactions;

  const columns = exportCSVColumns;
  const delimiter = ',';
  const transactionAsText = json2csv(transactionsRows, columns, delimiter);

  const csvFile = new Blob([transactionAsText], {
    type: 'text/csv;charset=utf-8',
  });

  return csvFile;
};

export const generateAsyncFile = async (
  list: StatementsPreparedToExport,
  fileType: SupportedFilesType,
  previousDay: string,
  previousDayValue: string,
  formattedTotalValue: string
): Promise<Blob> => {
  const generalErrorMessage =
    'There was an error trying to generate the file. Please try again.';
  try {
    return fileType === 'pdf'
      ? generateAsyncPDF(
          list || [],
          previousDay,
          previousDayValue,
          formattedTotalValue
        )
      : generateAsyncCSV(
          list || [],
          previousDay,
          previousDayValue,
          formattedTotalValue
        );
  } catch {
    // eslint-disable-next-line no-alert
    alert(generalErrorMessage);
    throw new Error(generalErrorMessage);
  }
};
